import config from "../config";
import { Language } from "../translations";
import {
  GalleryV2,
  PackageV2,
  FolderV2,
  FileV2,
  GalleryDataV2,
  PackageDataV2,
  PhotoV2,
} from "../types/v2";
import { getThumbnailUrl } from "../utils/thumbnail";

// ============================================================================
// URL Helpers
// ============================================================================

const getUrl = (path: string): string => {
  // Ensure path starts with / for absolute path resolution
  const absolutePath = path.startsWith("/") ? path : `/${path}`;
  return `${config.galleryBaseUrl}${absolutePath}`;
};

const getApiUrl = (path: string): string => {
  return `${config.apiBaseUrl}${path}`;
};

// ============================================================================
// File to Photo transformation
// ============================================================================

/**
 * Transform a file from the API into a Photo for UI components
 */
const fileToPhoto = (file: FileV2, galleryId: string): PhotoV2 => {
  const url = getUrl(file.storagePath);
  const thumbnailUrl = getUrl(getThumbnailUrl(file.storagePath));

  return {
    id: file.id,
    galleryId,
    url,
    thumbnailUrl,
    takenAt: file.takenAt || new Date().toISOString(), // Use EXIF date if available
    width: file.width || 1200,
    height: file.height || 800,
    type: file.type,
    filename: file.filename,
  };
};

/**
 * Recursively extract all files from folder tree as photos
 */
const extractPhotosFromFolders = (
  folders: FolderV2[],
  galleryId: string,
): PhotoV2[] => {
  const photos: PhotoV2[] = [];

  const processFolder = (folder: FolderV2) => {
    // Add files from this folder
    folder.files.forEach((file) => {
      photos.push(fileToPhoto(file, galleryId));
    });

    // Process subfolders recursively
    folder.subFolders.forEach(processFolder);
  };

  folders.forEach(processFolder);
  return photos;
};

// ============================================================================
// Gallery API
// ============================================================================

/**
 * Fetch gallery by shareId from MVP API
 */
export const fetchGalleryV2 = async (
  shareId: string,
  language: Language = "pl",
): Promise<GalleryDataV2 | null> => {
  try {
    console.log(`Fetching gallery from MVP API: ${shareId}`);

    const response = await fetch(
      getApiUrl(`/photos-eu/v2/galleries/${shareId}`),
    );

    if (!response.ok) {
      if (response.status === 404) {
        console.warn(`Gallery not found: ${shareId}`);
        return null;
      }
      if (response.status === 403) {
        const error = await response.json();
        console.warn(`Gallery unavailable: ${error.message}`);
        return null;
      }
      throw new Error(
        `Failed to fetch gallery: ${response.status} ${response.statusText}`,
      );
    }

    const gallery: GalleryV2 = await response.json();

    // Transform folders to add full URLs
    const folders = transformFolders(gallery.folders);

    // Extract photos from folder tree and sort by takenAt timestamp
    const photos = extractPhotosFromFolders(folders, gallery.id);
    photos.sort(
      (a, b) => new Date(a.takenAt).getTime() - new Date(b.takenAt).getTime(),
    );

    // Get title in preferred language
    const i18n = gallery.i18n || {};
    const langContent = i18n[language] || i18n["pl"] || i18n["en"];
    const title = langContent?.title || gallery.shareId;
    const description = langContent?.description;

    // Get event info
    const eventI18n = gallery.event?.i18n || {};
    const eventLangContent =
      eventI18n[language] || eventI18n["pl"] || eventI18n["en"];

console.log('!!!V2', {gallery, firstPhoto: photos[0]})
    const date = gallery.event?.date ?? photos[0]?.takenAt ?? gallery.folders[0]?.files[0]?.takenAt;
    return {
      id: gallery.id,
      shareId: gallery.shareId,
      title,
      description,
      date,
      expiresAt: gallery.expiresAt,
      isActive: gallery.isActive,
      folders,
      photos,
      eventTitle: eventLangContent?.title,
      eventDate: date,
      isMultilang: Object.keys(i18n).length > 1,
    };
  } catch (error) {
    console.error(`Error fetching gallery ${shareId}:`, error);
    return null;
  }
};

// ============================================================================
// Package API
// ============================================================================

/**
 * Fetch package by shareId from MVP API
 */
export const fetchPackageV2 = async (
  shareId: string,
): Promise<PackageDataV2 | null> => {
  try {
    console.log(`Fetching package from MVP API: ${shareId}`);

    const response = await fetch(getApiUrl(`/photos-eu/packages/${shareId}`));

    if (!response.ok) {
      if (response.status === 404) {
        console.warn(`Package not found: ${shareId}`);
        return null;
      }
      if (response.status === 403) {
        const error = await response.json();
        console.warn(`Package unavailable: ${error.message}`);
        return null;
      }
      throw new Error(
        `Failed to fetch package: ${response.status} ${response.statusText}`,
      );
    }

    const pkg: PackageV2 = await response.json();

    // Transform folders to add full URLs
    const folders = transformFolders(pkg.folders);

    // Extract photos from folder tree and sort by takenAt timestamp
    const photos = extractPhotosFromFolders(folders, pkg.id);
    photos.sort(
      (a, b) => new Date(a.takenAt).getTime() - new Date(b.takenAt).getTime(),
    );

    return {
      id: pkg.id,
      shareId: pkg.shareId,
      customerName: pkg.order?.customerName,
      language: pkg.order?.language,
      expiresAt: pkg.expiresAt,
      isActive: pkg.isActive,
      downloadCount: pkg.downloadCount,
      folders,
      photos,
    };
  } catch (error) {
    console.error(`Error fetching package ${shareId}:`, error);
    return null;
  }
};

/**
 * Get ZIP download URL for package
 */
export const getDownloadZipUrl = async (
  shareId: string,
): Promise<string | null> => {
  try {
    const response = await fetch(getApiUrl(`/photos-eu/packages/${shareId}/zip`));

    if (!response.ok) {
      console.warn("Failed to get ZIP URL");
      return null;
    }

    const data = await response.json();
    return data.downloadUrl;
  } catch (error) {
    console.error("Error getting ZIP URL:", error);
    return null;
  }
};

// ============================================================================
// Folder transformation
// ============================================================================

/**
 * Transform folder tree to add full URLs to file paths
 */
const transformFolders = (folders: FolderV2[]): FolderV2[] => {
  return folders.map((folder) => ({
    ...folder,
    coverFileUrl: folder.coverFileUrl ? getUrl(folder.coverFileUrl) : undefined,
    files: folder.files.map((file) => ({
      ...file,
      storagePath: getUrl(file.storagePath),
    })),
    subFolders: transformFolders(folder.subFolders),
  }));
};

// ============================================================================
// Timeline generation (reused from original service)
// ============================================================================

export interface TimePointV2 {
  time: string; // Format: HH:MM (display format)
  fullDateTime: string; // Format: YYYY-MM-DD HH:MM (unique identifier)
  isoDateTime: string;
  position: number; // Index position in the gallery
}

/**
 * Generate time points for timeline navigation
 * Creates hourly time points for photos that have valid takenAt timestamps
 */
export const generateTimePointsV2 = (photos: PhotoV2[]): TimePointV2[] => {
  if (photos.length === 0) return [];

  // Filter photos with valid takenAt and sort by time
  const photosWithTime = photos
    .filter((p) => p.takenAt && !isNaN(new Date(p.takenAt).getTime()))
    .sort((a, b) => new Date(a.takenAt).getTime() - new Date(b.takenAt).getTime());

  // If no photos have valid timestamps, return a single "All Photos" point
  if (photosWithTime.length === 0) {
    return [
      {
        time: "00:00",
        fullDateTime: "All Photos",
        isoDateTime: new Date().toISOString(),
        position: 0,
      },
    ];
  }

  const timePoints: TimePointV2[] = [];
  const firstPhoto = new Date(photosWithTime[0].takenAt);
  const lastPhoto = new Date(photosWithTime[photosWithTime.length - 1].takenAt);

  // Helper to format date in local time consistently
  const formatLocalDateTime = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    const hour = date.getHours().toString().padStart(2, "0");
    return `${year}-${month}-${day} ${hour}:00`;
  };

  // Map to track hours that have photos (as date strings in local time)
  const hoursWithPhotos = new Set<string>();

  // Identify which hours have photos
  photosWithTime.forEach((photo) => {
    const photoDate = new Date(photo.takenAt);
    hoursWithPhotos.add(formatLocalDateTime(photoDate));
  });

  // Set start time to the beginning of the hour
  const startHour = new Date(firstPhoto);
  startHour.setMinutes(0, 0, 0);

  // Create time points only for hours that have photos
  const currentHour = new Date(startHour);
  while (currentHour <= lastPhoto) {
    // Format as date+time to ensure uniqueness across multiple days (using local time)
    const fullDateTime = formatLocalDateTime(currentHour);

    // Only add time point if there are photos in this hour
    if (hoursWithPhotos.has(fullDateTime)) {
      const displayTime = `${currentHour.getHours().toString().padStart(2, "0")}:00`;

      // Find position of first photo after this time
      const position = photosWithTime.findIndex(
        (photo) => new Date(photo.takenAt) >= currentHour,
      );

      timePoints.push({
        time: displayTime,
        fullDateTime: fullDateTime,
        isoDateTime: currentHour.toISOString(),
        position: position !== -1 ? position : photosWithTime.length - 1,
      });
    }

    // Move to next hour
    currentHour.setHours(currentHour.getHours() + 1);
  }

  return timePoints;
};
